import { toast } from "sonner";

// Email templates for clients
const CLIENT_TEMPLATES = {
  REGISTRATION: {
    subject: "Welcome to Global Therapy Hub!",
    body: (firstName: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Welcome to Global Therapy Hub, ${firstName}!</h2>
        <p>Thank you for creating your account with us. You're now part of our global community dedicated to making mental health care accessible to everyone.</p>
        <p>With your new account, you can:</p>
        <ul>
          <li>Search for certified therapists worldwide</li>
          <li>Book online or in-person sessions</li>
          <li>Manage your appointments in one place</li>
          <li>Access resources to support your mental health journey</li>
        </ul>
        <p>If you have any questions or need assistance, our support team is here to help.</p>
        <p>Wishing you well on your journey,</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  },
  SESSION_BOOKED: {
    subject: "Your Therapy Session is Confirmed",
    body: (firstName: string, therapistName: string, date: string, time: string, sessionType: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Session Confirmed!</h2>
        <p>Hello ${firstName},</p>
        <p>Your session with <strong>${therapistName}</strong> has been successfully booked:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <p><strong>Date:</strong> ${date}</p>
          <p><strong>Time:</strong> ${time}</p>
          <p><strong>Session Type:</strong> ${sessionType}</p>
        </div>
        <p>Please make sure you're ready 5 minutes before your session starts.</p>
        <p>If you need to reschedule or cancel, please do so at least 24 hours in advance through your client portal.</p>
        <p>We wish you a productive session!</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  },
  SUBSCRIPTION_CREATED: {
    subject: "Your Therapy Subscription is Confirmed",
    body: (firstName: string, therapistName: string, frequency: string, startDate: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Subscription Confirmed!</h2>
        <p>Hello ${firstName},</p>
        <p>Your ${frequency} subscription with <strong>${therapistName}</strong> has been successfully created:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <p><strong>Starts on:</strong> ${startDate}</p>
          <p><strong>Frequency:</strong> ${frequency}</p>
        </div>
        <p>Your sessions have been scheduled in your calendar and you'll receive reminders before each appointment.</p>
        <p>You can manage your subscription from your client portal at any time.</p>
        <p>We wish you a transformative therapy journey!</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  },
  SESSION_REMINDER: {
    subject: "Reminder: Your Therapy Session is Tomorrow",
    body: (firstName: string, therapistName: string, date: string, time: string, sessionType: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Session Reminder</h2>
        <p>Hello ${firstName},</p>
        <p>This is a friendly reminder that you have a session with <strong>${therapistName}</strong> tomorrow:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <p><strong>Date:</strong> ${date}</p>
          <p><strong>Time:</strong> ${time}</p>
          <p><strong>Session Type:</strong> ${sessionType}</p>
        </div>
        <p>Please make sure you're ready 5 minutes before your session starts.</p>
        <p>We wish you a productive session!</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  }
};

// Email templates for therapists
const THERAPIST_TEMPLATES = {
  REGISTRATION: {
    subject: "Welcome to Global Therapy Hub Provider Network!",
    body: (firstName: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Welcome to Our Provider Network, ${firstName}!</h2>
        <p>Thank you for applying to join the Global Therapy Hub network of certified therapists.</p>
        <p>We're currently reviewing your application and certification from formacionpsicoterapia.com. This process typically takes 1-2 business days.</p>
        <p>Once approved, you'll be able to:</p>
        <ul>
          <li>Set up your complete therapist profile</li>
          <li>Configure your availability for online and in-person sessions</li>
          <li>Set your session rates</li>
          <li>Start receiving client bookings</li>
        </ul>
        <p>If we need any additional information, we'll contact you directly.</p>
        <p>We're excited to have you join our global network of mental health professionals!</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  },
  APPLICATION_APPROVED: {
    subject: "Your Application is Approved - Complete Your Profile",
    body: (firstName: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Application Approved!</h2>
        <p>Congratulations ${firstName}!</p>
        <p>We're pleased to inform you that your application to join the Global Therapy Hub network has been approved.</p>
        <p>To start receiving client bookings, please complete the following steps:</p>
        <ol>
          <li>Log in to your therapist portal</li>
          <li>Complete your professional profile with your expertise, approach, and a professional photo</li>
          <li>Set up your availability calendar</li>
          <li>Configure your session pricing</li>
        </ol>
        <p>The more detailed your profile, the better chance you have of connecting with clients who are the right fit for your practice.</p>
        <p>Welcome to our global community of certified mental health professionals!</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  },
  SESSION_BOOKED: {
    subject: "New Session Booked",
    body: (firstName: string, clientName: string, date: string, time: string, sessionType: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>New Session Booked!</h2>
        <p>Hello ${firstName},</p>
        <p>A new client has booked a session with you:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <p><strong>Client:</strong> ${clientName}</p>
          <p><strong>Date:</strong> ${date}</p>
          <p><strong>Time:</strong> ${time}</p>
          <p><strong>Session Type:</strong> ${sessionType}</p>
        </div>
        <p>You can view the full details and client information in your therapist portal.</p>
        <p>Please ensure you're prepared for this session. If you need to reschedule, please contact the client as soon as possible through the portal.</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  },
  SUBSCRIPTION_CREATED: {
    subject: "New Subscription Created",
    body: (firstName: string, clientName: string, frequency: string, startDate: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>New Subscription Created!</h2>
        <p>Hello ${firstName},</p>
        <p>A client has created a recurring subscription with you:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <p><strong>Client:</strong> ${clientName}</p>
          <p><strong>Starts on:</strong> ${startDate}</p>
          <p><strong>Frequency:</strong> ${frequency}</p>
        </div>
        <p>All sessions have been automatically added to your calendar. You can view the full schedule in your therapist portal.</p>
        <p>This represents a valuable ongoing commitment from your client. We recommend reviewing their profile before the first session.</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  },
  SESSION_REMINDER: {
    subject: "Reminder: You Have a Session Tomorrow",
    body: (firstName: string, clientName: string, date: string, time: string, sessionType: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2>Session Reminder</h2>
        <p>Hello ${firstName},</p>
        <p>This is a friendly reminder that you have a session tomorrow:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <p><strong>Client:</strong> ${clientName}</p>
          <p><strong>Date:</strong> ${date}</p>
          <p><strong>Time:</strong> ${time}</p>
          <p><strong>Session Type:</strong> ${sessionType}</p>
        </div>
        <p>Please make sure you're ready 5 minutes before the session starts.</p>
        <p><strong>The Global Therapy Hub Team</strong></p>
      </div>
    `
  }
};

// In a production app, this would connect to a real email service
const sendEmail = async (to: string, subject: string, body: string): Promise<boolean> => {
  console.log(`Sending email to: ${to}`);
  console.log(`Subject: ${subject}`);
  console.log(`Body: ${body}`);
  
  // Simulate API call to email service
  try {
    // In a real implementation, this would be an API call to your email service
    // Example: await fetch('https://api.emailservice.com/send', { method: 'POST', body: JSON.stringify({ to, subject, body }) })
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // For demonstration, we'll log success and return true
    console.log('Email sent successfully');
    return true;
  } catch (error) {
    console.error('Failed to send email:', error);
    toast("Email Notification Failed: We couldn't send the email notification. The system will retry automatically.");
    return false;
  }
};

// Client email functions
export const sendClientRegistrationEmail = async (email: string, firstName: string): Promise<boolean> => {
  const template = CLIENT_TEMPLATES.REGISTRATION;
  return sendEmail(email, template.subject, template.body(firstName));
};

export const sendClientSessionBookedEmail = async (
  email: string, 
  firstName: string, 
  therapistName: string, 
  date: string, 
  time: string, 
  sessionType: string
): Promise<boolean> => {
  const template = CLIENT_TEMPLATES.SESSION_BOOKED;
  return sendEmail(email, template.subject, template.body(firstName, therapistName, date, time, sessionType));
};

export const sendClientSubscriptionEmail = async (
  email: string, 
  firstName: string, 
  therapistName: string, 
  frequency: string, 
  startDate: string
): Promise<boolean> => {
  const template = CLIENT_TEMPLATES.SUBSCRIPTION_CREATED;
  return sendEmail(email, template.subject, template.body(firstName, therapistName, frequency, startDate));
};

export const sendClientSessionReminderEmail = async (
  email: string, 
  firstName: string, 
  therapistName: string, 
  date: string, 
  time: string, 
  sessionType: string
): Promise<boolean> => {
  const template = CLIENT_TEMPLATES.SESSION_REMINDER;
  return sendEmail(email, template.subject, template.body(firstName, therapistName, date, time, sessionType));
};

// Therapist email functions
export const sendTherapistRegistrationEmail = async (email: string, firstName: string): Promise<boolean> => {
  const template = THERAPIST_TEMPLATES.REGISTRATION;
  return sendEmail(email, template.subject, template.body(firstName));
};

export const sendTherapistApprovalEmail = async (email: string, firstName: string): Promise<boolean> => {
  const template = THERAPIST_TEMPLATES.APPLICATION_APPROVED;
  return sendEmail(email, template.subject, template.body(firstName));
};

export const sendTherapistSessionBookedEmail = async (
  email: string, 
  firstName: string, 
  clientName: string, 
  date: string, 
  time: string, 
  sessionType: string
): Promise<boolean> => {
  const template = THERAPIST_TEMPLATES.SESSION_BOOKED;
  return sendEmail(email, template.subject, template.body(firstName, clientName, date, time, sessionType));
};

export const sendTherapistSubscriptionEmail = async (
  email: string, 
  firstName: string, 
  clientName: string, 
  frequency: string, 
  startDate: string
): Promise<boolean> => {
  const template = THERAPIST_TEMPLATES.SUBSCRIPTION_CREATED;
  return sendEmail(email, template.subject, template.body(firstName, clientName, frequency, startDate));
};

export const sendTherapistSessionReminderEmail = async (
  email: string, 
  firstName: string, 
  clientName: string, 
  date: string, 
  time: string, 
  sessionType: string
): Promise<boolean> => {
  const template = THERAPIST_TEMPLATES.SESSION_REMINDER;
  return sendEmail(email, template.subject, template.body(firstName, clientName, date, time, sessionType));
};
